import React, { useState, useEffect, useRef } from 'react';
import { headerAPI, entityAPI } from '../../../services/api';
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Tag, X, AlertCircle, CheckCircle, XCircle, Hash, Shield, Pencil, Trash2, Plus, Eye, Filter, ChevronUp, ChevronDown } from 'lucide-react';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import Pagination from '../../../components/common/Pagination';

const Headers = () => {
  const [headers, setHeaders] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedHeaderId, setSelectedHeaderId] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editingHeader, setEditingHeader] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // Show all headers by default for client-side filtering
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({
    entity_id: '',
    header_name: '',
    header_type: 'promotional'
  });
  const [entitySearch, setEntitySearch] = useState('');
  // Popup-level error and success states
  const [popupError, setPopupError] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');
  
  // PROMPT-046-FIX-04: User role and entities state (supports multiple entities)
  const [userRole, setUserRole] = useState('');
  const [userEntities, setUserEntities] = useState([]);
  const [loadingUserEntities, setLoadingUserEntities] = useState(false);
  const [canAddHeader, setCanAddHeader] = useState(false);

  useEffect(() => {
    fetchHeaders();
    fetchUserRoleAndEntity();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterEntity, filterType, filterStatus, selectedHeaderId]);

  // Pagination handlers (client-side)
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // PROMPT-046-FIX-04: Fetch user role and entities (supports multiple entities)
  const fetchUserRoleAndEntity = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      const role = userInfo.role || '';
      setUserRole(role);

      if (role === 'master_admin' || role === 'MASTER_ADMIN') {
        // Master admin: fetch all entities and set full permissions
        setCanAddHeader(true);
        fetchEntities();
      } else {
        // User: fetch ALL mapped entities
        setLoadingUserEntities(true);
        const response = await entityAPI.getUserEntities();
        const entities = response.data || [];
        setUserEntities(entities);
        
        // PROMPT-044-FIX-01: Set permission for adding headers
        setCanAddHeader(entities.length > 0);
        
        // Auto-fill entity if user has exactly ONE entity
        if (entities.length === 1) {
          setFormData(prev => ({
            ...prev,
            entity_id: entities[0].entity_id.toString()
          }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch user entities:', err);
      if (userRole !== 'master_admin' && userRole !== 'MASTER_ADMIN') {
        setError('No entity assigned to your account');
      }
    } finally {
      setLoadingUserEntities(false);
    }
  };

  const fetchHeaders = async (page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      const response = await headerAPI.getAll(page, size);
      
      console.log('Headers API Response:', response.data);
      
      // PROMPT-098: Handle paginated response format
      let headersData = [];
      let total = 0;
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        // Format with pagination: { data: { data: [...], total: 356, page: 1, page_size: 20 } }
        headersData = response.data.data;
        total = response.data.total || 0;
      } else if (response.data?.headers && Array.isArray(response.data.headers)) {
        // Format from GetUserHeaders: { success: true, headers: [...] }
        headersData = response.data.headers;
        total = headersData.length;
      } else if (Array.isArray(response.data)) {
        // Direct array format
        headersData = response.data;
        total = headersData.length;
      }
      
      console.log('Headers Data:', headersData.length, 'Total:', total);
      
      setHeaders(headersData);
      setTotalRecords(total);
      setTotalPages(Math.ceil(total / size));
      
      console.log('Pagination State - totalRecords:', total, 'totalPages:', Math.ceil(total / size));
    } catch (error) {
      console.error('Failed to fetch headers:', error);
      setError('Failed to fetch headers');
      setHeaders([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    try {
      const response = await entityAPI.getAll();
      setEntities(response.data?.filter(e => e.is_active) || []);
    } catch (error) {
      console.error('Failed to fetch entities');
    }
  };

  // PROMPT-099: Sanitize header name based on type
  const sanitizeHeaderName = (headerName, headerType) => {
    if (!headerName) return headerName;
    
    if (headerType === 'promotional') {
      // Promotional: Keep only digits (0-9), max 6
      const cleaned = headerName.replace(/[^0-9]/g, '');
      return cleaned.substring(0, 6);
    } else {
      // Service/Transactional: Keep only letters (A-Z), uppercase, max 6
      const cleaned = headerName.replace(/[^A-Za-z]/g, '');
      return cleaned.toUpperCase().substring(0, 6);
    }
  };

  // PROMPT-099: Restrict Header Name input at typing level based on type
  const handleHeaderNameChange = (e) => {
    const sanitized = sanitizeHeaderName(e.target.value, formData.header_type);
    setFormData({ ...formData, header_name: sanitized });
  };

  const handleHeaderNamePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const sanitized = sanitizeHeaderName(pastedText, formData.header_type);
    setFormData({ ...formData, header_name: sanitized });
  };

  const handleHeaderNameBlur = (e) => {
    const sanitized = sanitizeHeaderName(e.target.value, formData.header_type);
    setFormData({ ...formData, header_name: sanitized });
  };

  const handleHeaderTypeChange = (newType) => {
    // When type changes, re-sanitize the header name with new rules
    const sanitized = sanitizeHeaderName(formData.header_name, newType);
    setFormData({ ...formData, header_type: newType, header_name: sanitized });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPopupError('');
    setPopupSuccess('');

    // PROMPT-099: Sanitize header name before sending to backend
    const sanitizedHeaderName = sanitizeHeaderName(formData.header_name, formData.header_type);

    try {
      if (isEditMode && editingHeader) {
        // PROMPT-063: Update existing header
        await headerAPI.update(editingHeader.id, {
          entity_id: formData.entity_id,
          header_name: sanitizedHeaderName,
          header_type: formData.header_type
        });
        setPopupSuccess('Header updated successfully');
        // Close modal after a short delay to show success message
        setTimeout(() => {
          handleCloseModal();
          fetchHeaders();
        }, 1000);
      } else {
        // Create new header
        await headerAPI.create({
          entity_id: formData.entity_id,
          header_name: sanitizedHeaderName,
          header_type: formData.header_type
        });
        setPopupSuccess('Header created successfully');
        // Close modal after a short delay to show success message
        setTimeout(() => {
          handleCloseModal();
          fetchHeaders();
        }, 1000);
      }
    } catch (error) {
      // PROMPT-105: Handle duplicate header conflict (409)
      if (error.response?.status === 409) {
        setPopupError('Header already exists for the selected Entity.');
      } else if (error.response?.data?.error) {
        setPopupError(error.response.data.error);
      } else {
        setPopupError(isEditMode ? 'Failed to update header' : 'Failed to create header');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this header?')) return;
    
    try {
      await headerAPI.delete(id);
      setSuccess('Header deleted successfully');
      fetchHeaders();
    } catch (error) {
      setError('Failed to delete header');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHeader(null);
    setIsEditMode(false);
    setFormData({
      entity_id: '',
      header_name: '',
      header_type: 'promotional'
    });
    // Clear popup-level states
    setPopupError('');
    setPopupSuccess('');
    // Don't clear page-level states as they might be used for other operations
  };

  // PROMPT-063: Handle edit header functionality
  const handleEdit = async (header) => {
    try {
      setLoading(true);
      const response = await headerAPI.getById(header.id);
      const headerData = response.data;
      
      setEditingHeader(headerData);
      setIsEditMode(true);
      setFormData({
        entity_id: headerData.entity_id?.toString() || '',
        header_name: headerData.header_name || '',
        header_type: headerData.header_type || 'promotional'
      });
      // Clear popup states when opening edit modal
      setPopupError('');
      setPopupSuccess('');
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch header details:', error);
      setError('Failed to load header details for editing');
    } finally {
      setLoading(false);
    }
  };

  // PROMPT-064: Use server-resolved entity_name, fallback only if truly missing
  const getEntityName = (header) => {
    // Backend should always provide entity_name
    if (header.entity_name) {
      return header.entity_name;
    }
    // Fallback to client-side lookup only if backend didn't provide it
    const entity = entities.find(e => e.id === header.entity_id) || 
                   userEntities.find(e => e.id === header.entity_id);
    return entity ? entity.entity_name : 'Entity not found';
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      promotional: 'bg-purple-100 text-purple-800',
      service_explicit: 'bg-blue-100 text-blue-800',
      service_implicit: 'bg-green-100 text-green-800',
      transactional: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const labels = {
      promotional: 'Promotional',
      service_explicit: 'Service Explicit',
      service_implicit: 'Service Implicit',
      transactional: 'Transactional'
    };
    return labels[type] || type;
  };

  // Generate dropdown options
  const headerOptions = [
    { value: '', label: 'All Headers' },
    ...(Array.isArray(headers) ? headers : []).map(h => ({
      value: h.id.toString(),
      label: `${h.header_name} (${getEntityName(h)})`
    }))
  ];

  const entityOptions = [
    { value: '', label: 'All Entities' },
    ...entities.map(e => ({
      value: e.id.toString(),
      label: e.entity_name
    }))
  ];

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'service_explicit', label: 'Service Explicit' },
    { value: 'service_implicit', label: 'Service Implicit' },
    { value: 'transactional', label: 'Transactional' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  // Filter headers
  const filteredHeaders = headers.filter(header => {
    const matchesHeader = !selectedHeaderId || header.id.toString() === selectedHeaderId;
    const matchesEntity = !filterEntity || header.entity_id.toString() === filterEntity;
    const matchesType = !filterType || header.header_type === filterType;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && header.is_active) ||
      (filterStatus === 'inactive' && !header.is_active);

    return matchesHeader && matchesEntity && matchesType && matchesStatus;
  });

  // Client-side pagination on filtered results
  const clientTotalPages = Math.ceil(filteredHeaders.length / pageSize) || 1;
  const paginatedHeaders = filteredHeaders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

  const activeCount = filteredHeaders.filter(h => h.is_active).length;
  const inactiveCount = filteredHeaders.filter(h => !h.is_active).length;

  const getStatusStyle = (isActive) => {
    if (isActive) return { bg: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: 'rgba(16, 185, 129, 0.2)' };
    return { bg: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: 'rgba(239, 68, 68, 0.2)' };
  };

  // Filter panel state
  const [filtersOpen, setFiltersOpen] = useState(true);
  const activeFilterCount = [selectedHeaderId, filterEntity, filterType, filterStatus].filter(v => v && v.trim() !== '').length;

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Tag className="w-5 h-5 text-white" />
            </div>
            Header Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage DLT sender headers for your entities</p>
        </div>
        {canAddHeader ? (
          <button
            onClick={() => {
              setPopupError('');
              setPopupSuccess('');
              // Auto-fill entity_id for single-entity users
              if (userEntities.length === 1) {
                setFormData(prev => ({
                  ...prev,
                  entity_id: userEntities[0].entity_id.toString()
                }));
              }
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-sm transition-all duration-200 hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
          >
            <Plus className="w-4 h-4" />
            Add Header
          </button>
        ) : (
          <div className="relative group">
            <button
              disabled
              className="flex items-center gap-2 bg-gray-300 text-gray-500 px-4 py-2 rounded-xl cursor-not-allowed text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Header
            </button>
            <div className="absolute right-0 top-full mt-2 w-64 p-2 bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
              You do not have permission to add headers for this entity.
            </div>
          </div>
        )}
      </div>

      {/* Collapsible Filter Panel */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/50 transition-colors cursor-pointer select-none"
          style={{ background: 'rgba(99, 102, 241, 0.02)' }}
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: '#6366f1' }} />
            <span className="text-sm font-semibold text-gray-700">Filters</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white rounded-full" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                {activeFilterCount}
              </span>
            )}
          </div>
          {filtersOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {filtersOpen && (
          <div style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-3 p-4">
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Header</label>
                <SearchableDropdown
                  options={headerOptions}
                  value={selectedHeaderId}
                  onChange={setSelectedHeaderId}
                  placeholder="All Headers"
                  clearable={true}
                />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Entity</label>
                <SearchableDropdown
                  options={entityOptions}
                  value={filterEntity}
                  onChange={setFilterEntity}
                  placeholder="All Entities"
                  clearable={true}
                />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Type</label>
                <SearchableDropdown
                  options={typeOptions}
                  value={filterType}
                  onChange={setFilterType}
                  placeholder="All Types"
                  clearable={true}
                />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Status</label>
                <SearchableDropdown
                  options={statusOptions}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  placeholder="All Status"
                  clearable={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>
            <AlertCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Success Display */}
      {success && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-800">{success}</p>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Summary Stat Cards */}
      {!loading && filteredHeaders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
              <Hash className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">{filteredHeaders.length}</div>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total Headers</div>
            </div>
          </div>
          <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">{activeCount}</div>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Active</div>
            </div>
          </div>
          <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.12)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>
              <XCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">{inactiveCount}</div>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Inactive</div>
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      {loading ? (
        <div className="bg-white rounded-xl p-16 flex flex-col items-center" style={cardStyle}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mb-3" style={{ borderColor: '#6366f1' }}></div>
          <p className="text-sm text-gray-500">Loading headers...</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
          {/* Table Header Info */}
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4" style={{ color: '#6366f1' }} />
              <span className="text-sm font-semibold text-gray-700">Results</span>
              {filteredHeaders.length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredHeaders.length)} of {filteredHeaders.length}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400">Page {currentPage} of {clientTotalPages}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Entity Name</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Header Name</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedHeaders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Tag className="w-8 h-8 text-gray-300" />
                        <p className="text-sm text-gray-400 font-medium">
                          {headers.length === 0 
                            ? "No headers available for this entity" 
                            : "No headers found matching your search criteria"
                          }
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedHeaders.map((header, index) => (
                    <tr key={header.id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                      <td className="px-4 py-3 text-xs text-gray-400 font-medium">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-gray-800">
                        {getEntityName(header)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-semibold text-gray-800">{header.header_name}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-[11px] font-semibold rounded-full ${getTypeBadgeColor(header.header_type)}`}>
                          {getTypeLabel(header.header_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {(() => {
                          const st = getStatusStyle(header.is_active);
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                              {header.is_active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              {header.is_active ? 'Active' : 'Inactive'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(header)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all hover:shadow-sm"
                            style={{ color: '#4f46e5', background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)' }}
                            title="Edit"
                          >
                            <Pencil className="w-3 h-3" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(header.id)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all hover:shadow-sm"
                            style={{ color: '#dc2626', background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.12)' }}
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {filteredHeaders.length > 0 && (
            <div style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecords={filteredHeaders.length}
                totalPages={clientTotalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.04), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  {isEditMode ? <Pencil className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{isEditMode ? 'Edit Header' : 'Create New Header'}</h3>
                  <p className="text-xs text-gray-500">{isEditMode ? 'Update header details' : 'Add a new DLT sender header'}</p>
                </div>
              </div>
              <button onClick={handleCloseModal} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Popup-level error and success messages */}
              {popupError && (
                <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{popupError}</p>
                </div>
              )}

              {popupSuccess && (
                <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">{popupSuccess}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* PROMPT-046-FIX-04: Role-based entity field with multi-entity support */}
                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Entity *</div>
                  {loadingUserEntities ? (
                    <div className="flex items-center gap-2 text-gray-600 py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                      <span className="text-sm">Loading entities...</span>
                    </div>
                  ) : userRole === 'master_admin' || userRole === 'MASTER_ADMIN' ? (
                    <SearchableDropdown
                      options={entities.map(e => ({
                        value: e.entity_id,
                        label: `${e.entity_name} (${e.entity_id})`
                      }))}
                      value={formData.entity_id}
                      onChange={(value) => setFormData({ ...formData, entity_id: value })}
                      placeholder="Select Entity *"
                      clearable={false}
                    />
                  ) : userEntities.length === 0 ? (
                    <div className="text-sm text-red-600">
                      No entity assigned to your account
                    </div>
                  ) : userEntities.length === 1 ? (
                    <div>
                      <input
                        type="text"
                        value={userEntities[0].entity_name}
                        readOnly
                        disabled
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Entity is determined by your account mapping
                      </p>
                    </div>
                  ) : (
                    <SearchableDropdown
                      options={userEntities.map(e => ({
                        value: e.entity_id,
                        label: `${e.entity_name} (${e.entity_id_string})`
                      }))}
                      value={formData.entity_id}
                      onChange={(value) => setFormData({ ...formData, entity_id: value })}
                      placeholder="Select Entity *"
                      clearable={false}
                    />
                  )}
                </div>

                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Header Type *</div>
                  <SearchableDropdown
                    options={[
                      { value: 'promotional', label: 'Promotional' },
                      { value: 'service_explicit', label: 'Service Explicit' },
                      { value: 'service_implicit', label: 'Service Implicit' },
                      { value: 'transactional', label: 'Transactional' }
                    ]}
                    value={formData.header_type}
                    onChange={handleHeaderTypeChange}
                    placeholder="Select Type *"
                    clearable={false}
                  />
                </div>

                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Header Name *</div>
                  <input
                    type="text"
                    required
                    value={formData.header_name}
                    onChange={handleHeaderNameChange}
                    onPaste={handleHeaderNamePaste}
                    onBlur={handleHeaderNameBlur}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder={formData.header_type === 'promotional' ? 'Max 6 digits' : 'Max 6 letters (A-Z)'}
                  />
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-white transition-all hover:bg-gray-50"
                    style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={userRole !== 'master_admin' && userRole !== 'MASTER_ADMIN' && userEntities.length === 0}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
                  >
                    {isEditMode ? 'Update Header' : 'Create Header'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Headers;
